// The help line under a field (spec/dashboard.md section 6.4): the imported parameter descriptions were written for
// another product ("look left", "InGame windows", "Visual Scripting", "In Seconds" next to a d/h/m/s widget), so the
// system templates' parameters get rewritten help in product language, keyed by the declaring template; anything else
// keeps its own description with the legacy sentences dropped.
import type { Parameter, Template } from '../api/types';

const ZERO_UNTIL_EVENT_ENDS = '0 = until the event ends.';
const VIEW = 'The view asset the game uses to show it.';
const PLACEMENT = 'Where the game shows its icon (main screen or shop, and which side).';
const DISPLAY_PRIORITY = 'Display order when the game shows several at once; higher comes first.';
const ICON = 'The icon the game shows for it.';

/** Rewritten help per declaring template, then per parameter name. */
const HELP: Record<string, Record<string, string>> = {
  'SmartObjects.GameOffer': {
    name: 'The offer title players see.',
    description: 'The text players see under the offer title.',
    sprite: 'The main picture of the offer window.',
    icon: ICON,
    storeItem: 'The store item this offer sells: its price and its reward.',
    duration: `How long the offer stays available once it starts (see Lifetime from). ${ZERO_UNTIL_EVENT_ENDS}`,
    lifetimeFrom: 'When the duration starts counting: Activation (as soon as the offer starts) or First impression (when the player first sees it).',
    limit: 'How many times a player can buy the offer; reaching it ends the offer. 0 = unlimited.',
    wait: 'The offer starts paused and waits for the game to activate it (for example once the player sees it); its duration starts then.',
    cooldown: 'With Reactivate on, how long after the offer ends before it shows again. 0 = none.',
    reactivate: 'When the offer ends (bought out or expired) while its event is still running, it shows again after the cooldown.',
    unnyPriority: DISPLAY_PRIORITY,
    unnyView: VIEW,
    unnyPlacement: PLACEMENT,
  },
  'SmartObjects.GameOfferGroup': {
    name: 'The group title players see.',
    icon: ICON,
    type: 'Which items can be bought: Unlimited (any, any number of times), Chain (in list order, each after the previous one), Only one (the first purchase ends the group) or Each once.',
    storeItems: 'The store items sold together in this group.',
    duration: `How long the group stays available once it starts. ${ZERO_UNTIL_EVENT_ENDS}`,
    wait: 'The group starts paused and waits for the game to activate it; its duration starts then.',
    unnyPriority: DISPLAY_PRIORITY,
    unnyView: VIEW,
    unnyPlacement: PLACEMENT,
  },
  'SmartObjects.GameEvent': {
    name: 'The event title players see.',
    description: 'A description of the event.',
    unnyIcon: ICON,
    condition: 'When the event starts: it starts for a player as soon as this condition is true.',
    finishType: 'What ends the event: Condition (the condition turns false), Duration (the duration runs out) or From script (only the game ends it).',
    duration: 'How long the event runs when it finishes by Duration. 0 = no time limit.',
    script: 'What starts with the event: an offer or an offer group, which ends when the event ends.',
    cooldown: 'After the event ends, how long before it can start again. 0 = none.',
    manualRemove: 'When the event finishes it stays listed as finished (its offer stays too) until the game removes it.',
    limit: 'How many times the event can start while its condition stays true. 0 = unlimited.',
    unnyPriority: DISPLAY_PRIORITY,
    unnyView: VIEW,
    unnyPlacement: PLACEMENT,
  },
  'SmartObjects.StoreItem': {
    name: 'The item name players see in the store.',
    sprite: 'The picture shown for this store item.',
    price: 'What the player pays: a product for real money, items for a soft purchase, or a number of ads.',
    reward: 'What the player gets after buying.',
    dynamicReward: 'Not used: the reward above is always granted.',
  },
  'SmartObjects.Item': {
    name: 'The item name players see.',
    unnyIcon: ICON,
    type: 'Currency, Item or Event; decides which inventory holds it.',
    hCPrice: 'The item’s value in hard currency.',
    maxStack: 'The most of this item one inventory slot holds. 0 = unlimited.',
    components: 'Extra data attached to the item, such as a bundle or decay.',
  },
  'SmartObjects.Price': {
    type: 'How the player pays: Hard (real money through a product), Soft (game items) or Ads (watching ads).',
    product: 'The product charged for a hard price; none makes it free.',
    isVirtual: 'The hard price is paid in the platform’s virtual currency.',
    items: 'The items taken from the player for a soft price.',
    ads: 'How many ads the player watches for an ads price.',
  },
  'SmartObjects.Reward': {
    items: 'The items and amounts the player receives.',
  },
  'SmartObjects.ItemWithAmount': {
    item: 'The item.',
    count: 'How many. A negative count means the item’s max stack.',
  },
  'SmartObjects.SegmentOption': {
    name: 'The segment name.',
    description: 'What this segment is for.',
    type: 'Default: players leave when the condition turns false. Can’t leave: once in, players stay in for good.',
    condition: 'Players whose profile passes this condition are in the segment.',
  },
  'LiveOps.RewardTable': {
    name: 'The table name.',
    drops: 'The possible drops; one is picked per roll, in proportion to its weight.',
    guaranteedDrop: 'Given on every roll in addition to the picked drop.',
    pityThreshold: 'After this many rolls without the rarest drop, the next roll gives it. 0 = off.',
  },
  'LiveOps.RewardDrop': {
    item: 'The item and amount this drop gives.',
    weight: 'The relative chance of this drop; 0 never drops.',
  },
  'LiveOps.DailyBonus': {
    name: 'The bonus name.',
    type: 'When the progress resets: Collect all (after the last reward), Skip (a missed day or the last reward), Calendar (each new month) or Current week (a missed day restarts the current block of 7).',
    condition: 'Players who pass this condition get this daily bonus.',
    rewards: 'One reward per day, claimed in order, one claim per day.',
    bonusReward: 'Given once all rewards are claimed: daily until the month changes with the Calendar type, as the last reward of the cycle otherwise.',
  },
  'LiveOps.Store.SaleCampaign': {
    name: 'The sale name.',
    multiplier: 'Multiplies the reward amounts of the sale’s store items while it runs; the price stays the same.',
    storeItems: 'The store items on sale; empty = the store’s multiplied store items.',
    gameEvent: 'When set, the sale runs only while this event is active.',
  },
  'SmartObjects.ConditionalTemplate': {
    condition: 'Players who pass this condition get this one; empty = everyone.',
    priority: 'When several pass, the highest priority wins.',
  },
  'SmartObjects.GameStoreBase': {
    store: 'The store pages, in display order.',
    unnyView: VIEW,
  },
  'SmartObjects.SmartConfig': {
    name: 'The store name.',
    offerProducts: 'The products offers can switch to for a discount: a discounted offer is charged the nearest of these prices.',
    multipliedStoreItems: 'The store items a sale multiplies when the sale lists no items of its own.',
    time: 'Time settings of the store.',
  },
  'SmartObjects.TimeConfig': {
    useLocalTime: 'Use the device’s local time instead of the server time.',
    uTCTimeOffset: 'The offset from UTC applied to the game time.',
  },
  'LiveOps.Store.Page': {
    name: 'The page title players see.',
    content: 'The slots of the page, in display order.',
    condition: 'The page shows for players who pass this condition; empty = everyone.',
    unnyView: VIEW,
  },
  'LiveOps.Store.Slot': {
    type: 'The badge the slot shows (Popular, Best, New, Limited, Free…).',
    storeItem: 'The store item this slot sells.',
    logic: 'Purchase rules: a cooldown, a limit per period or a first-purchase multiplier.',
    condition: 'The slot shows for players who pass this condition; empty = everyone.',
    unnyView: VIEW,
  },
  'LiveOps.Store.SlotLogicCooldown': {
    cooldown: 'After a purchase, how long before the slot can be bought again. 0 = none.',
  },
  'LiveOps.Store.SlotLogicPeriod': {
    limit: 'How many purchases per period. 0 = unlimited.',
    period: 'The length of one period. 0 = all time.',
  },
  'LiveOps.Store.SlotLogicMultiplier': {
    multiplier: 'Multiplies the reward amounts until the player’s first purchase of this item; the price stays the same.',
  },
  'SmartObjects.OverrideInfo': {
    name: 'The override name.',
    flag: 'Set when an A/B test created this override; edit it from the test.',
    groups: 'The groups of changes; each applies to the players who pass its condition.',
  },
  'SmartObjects.OverrideGroup': {
    condition: 'Players who pass this condition see these changes.',
    priority: 'When two groups change the same field, the higher priority wins.',
    comments: 'Notes for your team.',
    overrides: 'The field changes this group applies.',
  },
  // spec/liveops-engine.md section 11.2 (the engine's reading of each parameter)
  'SmartObjects.Analytics.ABTest': {
    name: 'The test name, for your team.',
    comments: 'Notes for your team.',
    status: 'Inactive (nobody joins; assignments are removed), Active (eligible players join), Collect (no new players; the assigned stay), Finished (the assigned get the best variant) or Finished without change (the assigned keep their variant).',
    condition: 'Only players who pass this condition can join; empty = everyone. A player already in stays in when it turns false.',
    variants: 'The variants and their weights; each joining player gets one in proportion to its weight.',
    bestVariant: 'The winning variant; when the test is Finished every assigned player switches to it.',
    targetUsers: 'The percentage (0–100) of eligible players who join the test; the rest are never assigned until the salt changes.',
    usersType: 'Which players can join: New (players new to the game), Old (returning players) or All.',
    concurrentTest: 'On: players can join this test while still in another unfinished test. Off: a player in another non-concurrent test is skipped.',
    finishTime: 'Once this time passes nobody joins and every assignment is removed. 0 = no end.',
    preInitLaunch: 'The game may start the test before its content has finished loading (the early start of the SDK).',
    salt: 'Changing it reshuffles which players join and which variant they get.',
    metric: 'The metric the test is judged by.',
    uplift: 'The smallest improvement worth detecting, for the sample size estimate.',
    alphaGlobal: 'The significance level of the sample size estimate.',
    betta: 'The miss rate (1 − power) of the sample size estimate.',
    preparationTime: 'The preparation time of the sample size estimate.',
    usersPerDay: 'Expected new players per day, for the duration estimate.',
    totalUsers: 'The estimated number of players the test needs.',
  },
  'SmartObjects.Analytics.ABTestVariant': {
    name: 'The variant name.',
    weight: 'The relative share of the test’s players who get this variant.',
  },
};

/** Sentences that only make sense in the other product (its navigation, its windows, its scripting, its SDK notes). */
const LEGACY = /look left|navigation|ingame|visual scripting|balancy|built-in|supported only by the new sdk/i;

/** The description without the sentences written for the other product; undefined when nothing is left. */
export function cleanDescription(text: string | null | undefined): string | undefined {
  if (!text) return undefined;
  const sentences = text.match(/[^.!?]+[.!?]*/g) ?? [];
  const kept = sentences.filter((s) => !LEGACY.test(s) && /[\p{L}\p{N}]/u.test(s));
  const out = kept.join(' ').replace(/\s+/g, ' ').trim();
  return out === '' ? undefined : out;
}

function lookup(template: string | null | undefined, name: string): string | undefined {
  return template ? HELP[template]?.[name] : undefined;
}

/** The help under a parameter's field: the rewritten system help (the declaring template, the owner, then its ancestors), else the cleaned description. */
export function parameterHelp(param: Parameter, owner: { template: string }, templates?: Map<string, Template>): string | undefined {
  const direct = lookup(param.declaredBy, param.name) ?? lookup(owner.template, param.name);
  if (direct) return direct;
  const seen = new Set<string>([owner.template]);
  let parent = templates?.get(owner.template)?.parent;
  while (parent && !seen.has(parent)) {
    seen.add(parent);
    const hit = lookup(parent, param.name);
    if (hit) return hit;
    parent = templates?.get(parent)?.parent;
  }
  return cleanDescription(param.description);
}
